import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  getFinPlanWithFact,
  getFinPlanSectionBlocks,
  getFinPlanFactTimesheet,
} from "../../../../api/projectEstimatesApi";
import "./PlanFactAnalysis.css";

const MONTH_NAMES_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const SECTION_GROUPS = [
  {
    id: "working_design",
    label: "РАБОЧЕЕ ПРОЕКТИРОВАНИЕ",
    key: "construction_design",
  },
  { id: "bim", label: "ВИМ КООРДИНИРОВАНИЕ", key: "bim_coordination" },
  { id: "quality", label: "КОНТРОЛЬ КАЧЕСТВА", key: "quality_control" },
  { id: "delivery", label: "ВЕДЕНИЕ ПРОЕКТА", key: "project_delivery" },
  {
    id: "management",
    label: "УПРАВЛЕНИЕ ПРОЕКТАМИ",
    key: "project_management",
  },
];

const formatMoney = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 0 })
    : "0";

const formatDays = (v) =>
  v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(1) : "0";

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const hasNonZeroCost = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0;
};

const getPerformerName = (performer) =>
  performer?.name ??
  performer?.employee?.name ??
  performer?.employee_name ??
  performer?.entity_name ??
  "Специалист";

const normalizeUnallocatedExpense = (value) => {
  if (value == null) return null;
  if (typeof value === "number") {
    return hasNonZeroCost(value)
      ? {
          name: "Нераспределенные расходы",
          fact_total_cost: value,
          sections: [],
        }
      : null;
  }
  if (typeof value === "object") {
    const factCost = toNumber(value.fact_total_cost);
    const reportsCost = toNumber(value.fact_reports_cost);
    const agreementsCost = toNumber(value.fact_agreements_cost);
    const sections = Array.isArray(value.sections) ? value.sections : [];
    const sectionsHaveData = sections.some(
      (section) =>
        hasNonZeroCost(section?.fact_total_cost) ||
        hasNonZeroCost(section?.fact_reports_cost) ||
        hasNonZeroCost(section?.fact_agreements_cost) ||
        (Array.isArray(section?.performers) && section.performers.length > 0),
    );
    if (
      factCost === 0 &&
      reportsCost === 0 &&
      agreementsCost === 0 &&
      !sectionsHaveData
    ) {
      return null;
    }
    return {
      name: value.name || "Нераспределенные расходы",
      fact_total_cost: factCost,
      fact_reports_cost: reportsCost,
      fact_agreements_cost: agreementsCost,
      fact_total_days: toNumber(value.fact_total_days),
      sections,
    };
  }
  return null;
};

const FactValue = ({ value, reports, agreements, prefix = "~ " }) => (
  <>
    {prefix}
    {formatMoney(value)}
    {hasNonZeroCost(agreements) && (
      <span className="plan-fact-fact-split">
        отчёты {formatMoney(reports)} · субподряд {formatMoney(agreements)}
      </span>
    )}
  </>
);

const pad2 = (n) => String(n).padStart(2, "0");

const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
};

const getMonthRange = (yyyyMm) => {
  const [year, month] = String(yyyyMm).split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return { dateFrom: null, dateTo: null };
  }
  const lastDay = new Date(year, month, 0).getDate();
  return {
    dateFrom: `${year}-${pad2(month)}-01`,
    dateTo: `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
};

const shiftYearMonth = (yyyyMm, dir) => {
  const [year, month] = String(yyyyMm).split("-").map(Number);
  const next = new Date(year, month - 1 + dir, 1);
  return `${next.getFullYear()}-${pad2(next.getMonth() + 1)}`;
};

const formatHours = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "0";
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const formatHoursCell = (v) => {
  if (v == null || v === "" || Number(v) === 0) return "";
  return formatHours(v);
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const isWeekend = (year, month, day) => {
  const dow = new Date(year, month - 1, day).getDay();
  return dow === 0 || dow === 6;
};

const toDateKey = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

const collectTimesheetMonths = (performers) => {
  const seen = new Map();
  (performers || []).forEach((p) => {
    (p.months || []).forEach((m) => {
      const year = Number(m.year);
      const month = Number(m.month);
      if (!Number.isFinite(year) || !Number.isFinite(month)) return;
      const key = `${year}-${pad2(month)}`;
      if (!seen.has(key)) seen.set(key, { year, month, key });
    });
  });
  return [...seen.values()].sort(
    (a, b) => a.year - b.year || a.month - b.month,
  );
};

const getPerformerNameFromTimesheet = (performer) =>
  performer?.employee?.name ??
  performer?.name ??
  performer?.employee_name ??
  "Специалист";

const FactTimesheetView = ({ data, loading, error, hideWhenEmpty = false }) => {
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());
  const performers = Array.isArray(data?.performers) ? data.performers : [];
  const months = collectTimesheetMonths(performers);

  const toggleMonth = (key) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (error) {
    return <div className="plan-fact-no-blocks">{error}</div>;
  }

  if (loading || data == null) {
    if (hideWhenEmpty) return null;
    return <div className="plan-fact-blocks-loading">Загрузка табеля...</div>;
  }

  if (!performers.length) {
    if (hideWhenEmpty) return null;
    return (
      <div className="plan-fact-no-blocks">Нет фактических трудозатрат</div>
    );
  }

  return (
    <div className="plan-fact-timesheet">
      <div className="plan-fact-timesheet-caption">Табель часов по отчётам</div>
      <table className="plan-fact-performers-table plan-fact-timesheet-summary">
        <thead>
          <tr>
            <th>Исполнитель</th>
            <th>Часы</th>
            <th>Дни</th>
          </tr>
        </thead>
        <tbody>
          {performers.map((p, idx) => {
            const employeeId = p.employee?.id ?? p.user_id ?? idx;
            const hours = toNumber(p.total_hours);
            const days =
              p.total_days != null ? toNumber(p.total_days) : hours / 8;
            return (
              <tr key={employeeId}>
                <td className="plan-fact-performer-name">
                  <span className="plan-fact-performer-bullet">○</span>
                  {getPerformerNameFromTimesheet(p)}
                </td>
                <td>{formatHours(hours)}</td>
                <td>{formatDays(days)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {months.map(({ year, month, key }) => {
        const daysCount = getDaysInMonth(year, month);
        const days = Array.from({ length: daysCount }, (_, i) => i + 1);
        const isCollapsed = collapsedMonths.has(key);
        const monthLabel = `${MONTH_NAMES_RU[month - 1] || month} ${year}`;
        const rows = performers
          .map((p, idx) => {
            const monthData = (p.months || []).find(
              (m) => Number(m.year) === year && Number(m.month) === month,
            );
            if (!monthData) return null;
            const hoursMap = {};
            (monthData.days || []).forEach((d) => {
              if (d?.date) hoursMap[d.date] = toNumber(d.hours);
            });
            return {
              id: p.employee?.id ?? p.user_id ?? idx,
              name: getPerformerNameFromTimesheet(p),
              hoursMap,
              total: toNumber(monthData.total_hours),
            };
          })
          .filter(Boolean);
        const monthTotal = rows.reduce((sum, row) => sum + row.total, 0);

        return (
          <div key={key} className="plan-fact-timesheet-month">
            <button
              type="button"
              className="plan-fact-timesheet-month-title"
              onClick={() => toggleMonth(key)}
            >
              <span
                className={`plan-fact-expand-icon ${
                  isCollapsed ? "" : "expanded"
                }`}
              >
                ▶
              </span>
              {monthLabel}
              <span className="plan-fact-timesheet-month-total">
                {formatHours(monthTotal)} ч
              </span>
            </button>
            {!isCollapsed && (
              <div className="plan-fact-timesheet-grid-wrap">
                <table className="plan-fact-performers-table plan-fact-timesheet-grid">
                  <thead>
                    <tr>
                      <th className="plan-fact-timesheet-name-col">
                        Исполнитель
                      </th>
                      {days.map((day) => (
                        <th
                          key={day}
                          className={
                            isWeekend(year, month, day)
                              ? "plan-fact-timesheet-weekend"
                              : ""
                          }
                        >
                          {day}
                        </th>
                      ))}
                      <th>Итого</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="plan-fact-timesheet-name-col">
                          {row.name}
                        </td>
                        {days.map((day) => {
                          const dateKey = toDateKey(year, month, day);
                          return (
                            <td
                              key={day}
                              className={
                                isWeekend(year, month, day)
                                  ? "plan-fact-timesheet-weekend"
                                  : ""
                              }
                            >
                              {formatHoursCell(row.hoursMap[dateKey])}
                            </td>
                          );
                        })}
                        <td>{formatHours(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const getPerformerFact = (performer) => {
  const reports = toNumber(performer?.fact_reports_cost);
  const agreements = toNumber(performer?.fact_agreements_cost);
  const hasFactCost =
    performer?.fact_cost != null ||
    hasNonZeroCost(reports) ||
    hasNonZeroCost(agreements);
  const factCost = hasFactCost
    ? performer?.fact_cost != null
      ? toNumber(performer.fact_cost)
      : reports + agreements
    : null;
  const factDays =
    performer?.fact_days != null
      ? toNumber(performer.fact_days)
      : performer?.fact_hours != null
        ? toNumber(performer.fact_hours) / 8
        : null;
  const planPercent =
    performer?.work_percent != null && Number(performer.work_percent) > 0
      ? Number(performer.work_percent)
      : null;
  const planDays =
    performer?.day_efforts != null && performer.day_efforts !== ""
      ? toNumber(performer.day_efforts)
      : null;
  return { reports, agreements, factCost, factDays, planPercent, planDays };
};

const getContractSectionsArray = (contractSections) => {
  if (!contractSections) return [];
  if (Array.isArray(contractSections)) return contractSections;
  if (Array.isArray(contractSections.sections)) {
    return contractSections.sections;
  }
  return [];
};

const getFactPeriodLoadingLabel = (selectedMonth) => {
  if (!selectedMonth) return "Загрузка факта за все периоды…";
  const [year, month] = String(selectedMonth).split("-").map(Number);
  const monthName = MONTH_NAMES_RU[month - 1];
  if (!monthName || !year) return "Загрузка факта…";
  return `Загрузка факта за ${monthName.toLowerCase()} ${year}…`;
};

const FactPeriodFilter = ({ selectedMonth, onChange, isRefreshing = false }) => {
  const currentYm = getCurrentYearMonth();
  const canGoForward = Boolean(selectedMonth) && selectedMonth < currentYm;

  return (
    <div className="plan-fact-period">
      <span className="plan-fact-period-label">Период факта</span>
      {isRefreshing && (
        <span className="plan-fact-period-status" aria-live="polite">
          <span className="plan-fact-spinner" />
          Загрузка…
        </span>
      )}
      <button
        type="button"
        className={`plan-fact-period-chip${selectedMonth == null ? " active" : ""}`}
        onClick={() => onChange(null)}
      >
        Все периоды
      </button>
      <div className="plan-fact-period-nav">
        <button
          type="button"
          className="plan-fact-period-nav-btn"
          onClick={() =>
            onChange(
              selectedMonth ? shiftYearMonth(selectedMonth, -1) : currentYm,
            )
          }
          aria-label="Предыдущий месяц"
        >
          ←
        </button>
        <input
          type="month"
          max={currentYm}
          className="plan-fact-period-input"
          value={selectedMonth || ""}
          onChange={(event) => onChange(event.target.value || null)}
          aria-label="Месяц факта"
        />
        <button
          type="button"
          className="plan-fact-period-nav-btn"
          disabled={!canGoForward}
          onClick={() => {
            if (!selectedMonth) return;
            const next = shiftYearMonth(selectedMonth, 1);
            if (next <= currentYm) onChange(next);
          }}
          aria-label="Следующий месяц"
        >
          →
        </button>
      </div>
    </div>
  );
};

const filterTimesheetByPerformers = (timesheet, performers) => {
  if (!timesheet) return timesheet;
  const allowedIds = new Set(
    (performers || [])
      .map((p) => p.employee?.id ?? p.employee_id ?? p.user_id)
      .filter((id) => id != null),
  );
  if (!allowedIds.size) {
    return { ...timesheet, performers: [] };
  }
  return {
    ...timesheet,
    performers: (timesheet.performers || []).filter((p) =>
      allowedIds.has(p.employee?.id ?? p.user_id),
    ),
  };
};

const RolePerformersTable = ({ section, showPlan = true }) => {
  const performers = Array.isArray(section.performers)
    ? section.performers
    : [];
  const planDays = toNumber(section.planDays);
  const planCost = toNumber(section.planEstimate);
  const factDays = toNumber(
    section.factDays ??
      (section.factHours != null ? Number(section.factHours) / 8 : 0),
  );
  const factCost = toNumber(section.factCost);
  const reportsCost = toNumber(section.factReportsCost);
  const agreementsCost = toNumber(section.factAgreementsCost);
  const devDays = showPlan ? planDays - factDays : factDays ? -factDays : 0;
  const devCost = showPlan ? planCost - factCost : -factCost;
  const performersAgreementsSum = performers.reduce(
    (sum, p) => sum + toNumber(p.fact_agreements_cost),
    0,
  );
  const unlistedAgreementsCost = agreementsCost - performersAgreementsSum;
  const hasAnything =
    performers.length > 0 ||
    hasNonZeroCost(factCost) ||
    hasNonZeroCost(planCost) ||
    hasNonZeroCost(agreementsCost);

  if (!hasAnything) {
    return (
      <div className="plan-fact-no-blocks">Нет фактических трудозатрат</div>
    );
  }

  return (
    <table className="plan-fact-performers-table">
      <thead>
        <tr>
          <th></th>
          <th>Исполнитель</th>
          <th>Трудозатраты</th>
          <th>Прямые затраты</th>
          <th>Трудозатраты</th>
          <th>Прямые затраты</th>
          <th>Трудозатраты</th>
          <th>Прямые затраты</th>
        </tr>
        <tr className="plan-fact-performers-subheader">
          <th></th>
          <th></th>
          <th colSpan={2} className="plan-fact-subheader-plan">
            План
          </th>
          <th colSpan={2} className="plan-fact-subheader-fact">
            Факт
          </th>
          <th colSpan={2} className="plan-fact-subheader-dev">
            Отклонение
          </th>
        </tr>
      </thead>
      <tbody>
        <tr className="plan-fact-block-total">
          <td></td>
          <td>Все исполнители</td>
          <td>{showPlan ? `= ${formatDays(planDays)}` : "–"}</td>
          <td>{showPlan ? `~ ${formatMoney(planCost)}` : "–"}</td>
          <td>= {formatDays(factDays)}</td>
          <td>
            <FactValue
              value={factCost}
              reports={reportsCost}
              agreements={agreementsCost}
            />
          </td>
          <td className={devDays >= 0 ? "positive" : "negative"}>
            = {formatDays(devDays)}
          </td>
          <td className={devCost >= 0 ? "positive" : "negative"}>
            ~ {devCost >= 0 ? "" : "-"}
            {formatMoney(Math.abs(devCost))}
          </td>
        </tr>
        {performers.map((p, pi) => {
          const {
            reports,
            agreements,
            factCost: pFactCost,
            factDays: pFactDays,
            planPercent,
            planDays: explicitPlanDays,
          } = getPerformerFact(p);
          const planDaysShare = showPlan
            ? explicitPlanDays != null
              ? explicitPlanDays
              : planPercent != null
                ? (planDays * planPercent) / 100
                : null
            : null;
          const planCostShare =
            showPlan && planPercent != null
              ? (planCost * planPercent) / 100
              : null;
          const hasFact = pFactDays != null || pFactCost != null;
          const pDevDays = showPlan
            ? planDaysShare != null && pFactDays != null
              ? planDaysShare - pFactDays
              : null
            : pFactDays != null
              ? -pFactDays
              : null;
          const pDevCost = showPlan
            ? planCostShare != null && pFactCost != null
              ? planCostShare - pFactCost
              : null
            : pFactCost != null
              ? -pFactCost
              : null;

          return (
            <tr
              key={p.employee?.id ?? p.employee_id ?? pi}
              className={`plan-fact-performer-row${
                hasNonZeroCost(agreements) ? " plan-fact-subcontract-row" : ""
              }`}
            >
              <td></td>
              <td className="plan-fact-performer-name">
                <span className="plan-fact-performer-bullet">○</span>
                {getPerformerName(p)}
                {planPercent != null && (
                  <span className="plan-fact-performer-share">
                    доля {planPercent}%
                  </span>
                )}
                {hasNonZeroCost(agreements) && (
                  <span className="plan-fact-performer-share">субподряд</span>
                )}
              </td>
              <td>
                {planDaysShare != null ? `= ${formatDays(planDaysShare)}` : "–"}
              </td>
              <td>
                {planCostShare != null
                  ? `~ ${formatMoney(planCostShare)}`
                  : "–"}
              </td>
              {!hasFact ? (
                <>
                  <td>–</td>
                  <td>–</td>
                  <td>–</td>
                  <td>–</td>
                </>
              ) : (
                <>
                  <td>
                    {pFactDays != null ? `= ${formatDays(pFactDays)}` : "–"}
                  </td>
                  <td>
                    {pFactCost != null ? (
                      <FactValue
                        value={pFactCost}
                        reports={reports}
                        agreements={agreements}
                      />
                    ) : (
                      "–"
                    )}
                  </td>
                  <td
                    className={
                      pDevDays != null
                        ? pDevDays >= 0
                          ? "positive"
                          : "negative"
                        : ""
                    }
                  >
                    {pDevDays != null ? `= ${formatDays(pDevDays)}` : "–"}
                  </td>
                  <td
                    className={
                      pDevCost != null
                        ? pDevCost >= 0
                          ? "positive"
                          : "negative"
                        : ""
                    }
                  >
                    {pDevCost != null
                      ? `~ ${pDevCost >= 0 ? "" : "-"}${formatMoney(
                          Math.abs(pDevCost),
                        )}`
                      : "–"}
                  </td>
                </>
              )}
            </tr>
          );
        })}
        {hasNonZeroCost(unlistedAgreementsCost) && (
          <tr className="plan-fact-performer-row plan-fact-subcontract-row">
            <td></td>
            <td className="plan-fact-performer-name">
              <span className="plan-fact-performer-bullet">○</span>
              Субподрядчики
            </td>
            <td>–</td>
            <td>–</td>
            <td>–</td>
            <td>~ {formatMoney(unlistedAgreementsCost)}</td>
            <td>–</td>
            <td>–</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const PlanFactAnalysis = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [dataPeriodKey, setDataPeriodKey] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [sectionBlocks, setSectionBlocks] = useState({});
  const [sectionBlocksLoading, setSectionBlocksLoading] = useState({});
  const [sectionTimesheets, setSectionTimesheets] = useState({});
  const [sectionTimesheetsLoading, setSectionTimesheetsLoading] = useState({});
  const [sectionTimesheetsError, setSectionTimesheetsError] = useState({});

  const { dateFrom, dateTo } = useMemo(
    () =>
      selectedMonth
        ? getMonthRange(selectedMonth)
        : { dateFrom: null, dateTo: null },
    [selectedMonth],
  );

  const periodKey = dateFrom && dateTo ? `${dateFrom}_${dateTo}` : "all";
  const isRefreshing = Boolean(planData) && dataPeriodKey !== periodKey;
  const detailKey = useCallback(
    (sectionId) => `${periodKey}:${sectionId}`,
    [periodKey],
  );
  const expandedMetaRef = useRef(new Map());

  const contractEstimateTotal = useMemo(() => {
    return planData?.cost_price ?? 0;
  }, [planData]);

  const factTotal = useMemo(() => {
    return planData?.fact_total_cost ?? 0;
  }, [planData]);

  const factReportsCost = useMemo(() => {
    return planData?.fact_reports_cost ?? 0;
  }, [planData]);

  const factAgreementsCost = useMemo(() => {
    return planData?.fact_agreements_cost ?? 0;
  }, [planData]);

  const deviationTotal = contractEstimateTotal - factTotal;

  const unallocatedExpense = useMemo(
    () => normalizeUnallocatedExpense(planData?.unallocated_expense),
    [planData],
  );

  const buildSectionsTree = useMemo(() => {
    if (!planData) return [];

    const cd = planData.construction_design || [];
    const cdProject = planData.construction_design_project || [];
    const allConstructionDesign = [...cd, ...cdProject];

    const groups = SECTION_GROUPS.map((gr) => {
      let sections = [];
      if (gr.key === "construction_design") {
        sections = allConstructionDesign.map((s) => {
          const sectionId = s.section?.id;
          const planEstimate = s.money_expenditures?.planned ?? 0;
          const planDays = s.day_efforts?.total ?? 0;
          return {
            id: `wd-${sectionId}`,
            sectionId,
            name: s.section?.name ?? "",
            planEstimate,
            planDays,
            factCost: s.fact_total_cost ?? 0,
            factReportsCost: s.fact_reports_cost ?? 0,
            factAgreementsCost: s.fact_agreements_cost ?? 0,
            factDays: s.fact_total_days ?? 0,
            factHours: s.fact_total_hours ?? 0,
            isWorkingDesign: true,
          };
        });
      } else {
        const groupData = planData[gr.key];
        const roles = groupData?.roles || [];
        sections = roles.map((r, idx) => ({
          id: `${gr.id}-${r.section_id ?? idx}`,
          sectionId: r.section_id,
          name: r.role ?? r.name ?? "",
          planEstimate: r.money_expenditures?.direct ?? r.total ?? 0,
          planDays: r.day_efforts?.direct ?? 0,
          factCost: r.fact_total_cost ?? 0,
          factReportsCost: r.fact_reports_cost ?? 0,
          factAgreementsCost: r.fact_agreements_cost ?? 0,
          factDays: r.fact_total_days ?? 0,
          factHours: r.fact_total_hours ?? 0,
          performers: Array.isArray(r.performers) ? r.performers : [],
          isWorkingDesign: false,
        }));
      }
      const planEstimate = sections.reduce(
        (sum, item) => sum + toNumber(item.planEstimate),
        0,
      );
      const factCost = sections.reduce(
        (sum, item) => sum + toNumber(item.factCost),
        0,
      );
      const factReportsCost = sections.reduce(
        (sum, item) => sum + toNumber(item.factReportsCost),
        0,
      );
      const factAgreementsCost = sections.reduce(
        (sum, item) => sum + toNumber(item.factAgreementsCost),
        0,
      );
      return {
        ...gr,
        sections,
        planEstimate,
        factCost,
        factReportsCost,
        factAgreementsCost,
      };
    });

    const contractSections = getContractSectionsArray(
      planData.contract_sections,
    ).map((section, idx) => {
      const sectionId = section.section?.id ?? section.section_id ?? section.id;
      const performers = Array.isArray(section.performers)
        ? section.performers
        : [];
      const planDays = performers.reduce(
        (sum, performer) => sum + toNumber(performer.day_efforts),
        0,
      );
      return {
        id: `cs-${sectionId ?? "x"}-${idx}`,
        sectionId,
        name:
          section.section?.name ??
          section.name ??
          section.section_name ??
          "Раздел",
        planEstimate: toNumber(section.amount),
        planDays,
        factCost: toNumber(section.fact_total_cost),
        factReportsCost: toNumber(section.fact_reports_cost),
        factAgreementsCost: toNumber(section.fact_agreements_cost),
        factDays: toNumber(section.fact_total_days),
        factHours: toNumber(section.fact_total_hours),
        performers,
        isWorkingDesign: false,
        skipTimesheet: true,
      };
    });

    if (contractSections.length > 0) {
      groups.push({
        id: "contract_sections",
        label: "РАЗДЕЛЫ НА ДОГОВОРЕ",
        sections: contractSections,
        planEstimate: contractSections.reduce(
          (sum, section) => sum + toNumber(section.planEstimate),
          0,
        ),
        factCost: contractSections.reduce(
          (sum, section) => sum + toNumber(section.factCost),
          0,
        ),
        factReportsCost: contractSections.reduce(
          (sum, section) => sum + toNumber(section.factReportsCost),
          0,
        ),
        factAgreementsCost: contractSections.reduce(
          (sum, section) => sum + toNumber(section.factAgreementsCost),
          0,
        ),
      });
    }

    return groups;
  }, [planData]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const requestedPeriod = periodKey;
    getFinPlanWithFact(projectId, dateFrom, dateTo)
      .then((data) => {
        if (!cancelled) {
          setPlanData(data);
          setDataPeriodKey(requestedPeriod);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Не удалось загрузить данные");
          setDataPeriodKey(requestedPeriod);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, dateFrom, dateTo, periodKey]);

  useEffect(() => {
    setExpandedSections(new Set());
    setExpandedGroups(new Set());
    setDataPeriodKey(null);
    expandedMetaRef.current = new Map();
    setSectionBlocks({});
    setSectionBlocksLoading({});
    setSectionTimesheets({});
    setSectionTimesheetsLoading({});
    setSectionTimesheetsError({});
  }, [projectId]);

  const loadSectionBlocks = useCallback(
    async (sectionId) => {
      if (!projectId || sectionId == null) return;
      const key = detailKey(sectionId);
      if (sectionBlocks[key] || sectionBlocksLoading[key]) return;

      setSectionBlocksLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const data = await getFinPlanSectionBlocks(
          projectId,
          sectionId,
          dateFrom,
          dateTo,
        );
        setSectionBlocks((prev) => ({ ...prev, [key]: data }));
      } catch (err) {
        console.error("Error loading section blocks:", err);
      } finally {
        setSectionBlocksLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [
      projectId,
      dateFrom,
      dateTo,
      detailKey,
      sectionBlocks,
      sectionBlocksLoading,
    ],
  );

  const loadSectionTimesheet = useCallback(
    async (sectionId) => {
      if (!projectId || sectionId == null) return;
      const key = detailKey(sectionId);
      if (sectionTimesheets[key] || sectionTimesheetsLoading[key]) return;

      setSectionTimesheetsLoading((prev) => ({ ...prev, [key]: true }));
      setSectionTimesheetsError((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      try {
        const data = await getFinPlanFactTimesheet(projectId, {
          sectionId,
          dateFrom,
          dateTo,
        });
        setSectionTimesheets((prev) => ({ ...prev, [key]: data }));
      } catch (err) {
        console.error("Error loading section timesheet:", err);
        setSectionTimesheetsError((prev) => ({
          ...prev,
          [key]: err?.message || "Не удалось загрузить табель",
        }));
      } finally {
        setSectionTimesheetsLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [
      projectId,
      dateFrom,
      dateTo,
      detailKey,
      sectionTimesheets,
      sectionTimesheetsLoading,
    ],
  );

  useEffect(() => {
    expandedMetaRef.current.forEach((meta) => {
      if (meta.sectionId == null || meta.skipTimesheet) return;
      if (meta.isWorkingDesign) {
        loadSectionBlocks(meta.sectionId);
      } else {
        loadSectionTimesheet(meta.sectionId);
      }
    });
  }, [periodKey, loadSectionBlocks, loadSectionTimesheet]);

  const toggleGroup = (id) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSection = (sec) => {
    const canExpand =
      sec.isWorkingDesign ||
      sec.sectionId != null ||
      (sec.performers && sec.performers.length > 0) ||
      hasNonZeroCost(sec.factCost) ||
      hasNonZeroCost(sec.planEstimate);
    if (!canExpand) return;
    const rowId = sec.id;
    const isCurrentlyExpanded = expandedSections.has(rowId);
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
    if (!isCurrentlyExpanded) {
      expandedMetaRef.current.set(rowId, {
        sectionId: sec.sectionId,
        isWorkingDesign: sec.isWorkingDesign,
        skipTimesheet: Boolean(sec.skipTimesheet),
      });
      if (sec.sectionId != null && !sec.skipTimesheet) {
        if (sec.isWorkingDesign) {
          loadSectionBlocks(sec.sectionId);
        } else {
          loadSectionTimesheet(sec.sectionId);
        }
      }
    } else {
      expandedMetaRef.current.delete(rowId);
    }
  };

  if (!projectId) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-empty">Выберите проект</div>
      </div>
    );
  }

  if (loading && !planData) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-loading">Загрузка...</div>
      </div>
    );
  }

  if (error && !planData) {
    return (
      <div className="plan-fact-page">
        <div className="plan-fact-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="plan-fact-page">
      <div className="plan-fact-header">
        <h2 className="plan-fact-title">План-фактный анализ</h2>
        <FactPeriodFilter
          selectedMonth={selectedMonth}
          onChange={setSelectedMonth}
          isRefreshing={isRefreshing}
        />
      </div>

      {error && <div className="plan-fact-error">{error}</div>}

      <div
        className={`plan-fact-content${isRefreshing ? " is-refreshing" : ""}`}
        aria-busy={isRefreshing}
      >
        {isRefreshing && (
          <div className="plan-fact-refresh-overlay" role="status">
            <div className="plan-fact-refresh-badge">
              <span className="plan-fact-spinner plan-fact-spinner--lg" />
              <span>{getFactPeriodLoadingLabel(selectedMonth)}</span>
            </div>
          </div>
        )}

      <div className="plan-fact-cards">
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Смета по договору</span>
          <span className="plan-fact-card-icon">📋</span>
          <span className="plan-fact-card-value">
            {formatMoney(contractEstimateTotal)}
          </span>
        </div>
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Факт</span>
          <span className="plan-fact-card-icon">📊</span>
          <span className="plan-fact-card-value plan-fact-card-value--fact">
            <FactValue
              value={factTotal}
              reports={factReportsCost}
              agreements={factAgreementsCost}
            />
          </span>
        </div>
        <div className="plan-fact-card">
          <span className="plan-fact-card-label">Отклонение</span>
          <span className="plan-fact-card-icon">📈</span>
          <span
            className={`plan-fact-card-value plan-fact-card-value--deviation ${
              deviationTotal >= 0 ? "positive" : "negative"
            }`}
          >
            ~ {deviationTotal >= 0 ? "" : "-"}
            {formatMoney(Math.abs(deviationTotal))}
          </span>
        </div>
      </div>

      <div className="plan-fact-table-wrap">
        <table className="plan-fact-table">
          <thead>
            <tr>
              <th className="plan-fact-th-section">Раздел</th>
              <th className="plan-fact-th-num">Смета по договору</th>
              <th className="plan-fact-th-num">Факт</th>
              <th className="plan-fact-th-num">Отклонение от сметы</th>
            </tr>
          </thead>
          <tbody>
            {buildSectionsTree.map((group) => {
              const groupPlan = toNumber(group.planEstimate);
              const groupFact = toNumber(group.factCost);
              const groupDev = groupPlan - groupFact;
              return (
              <React.Fragment key={group.id}>
                <tr
                  className="plan-fact-group-row"
                  onClick={() => toggleGroup(group.id)}
                >
                  <td className="plan-fact-td-section">
                    <span
                      className={`plan-fact-expand-icon ${
                        expandedGroups.has(group.id) ? "expanded" : ""
                      }`}
                    >
                      ▶
                    </span>
                    {group.label}
                  </td>
                  <td className="plan-fact-td-num">
                    {formatMoney(groupPlan)}
                  </td>
                  <td className="plan-fact-td-num plan-fact-fact-cell">
                    <FactValue
                      value={groupFact}
                      reports={group.factReportsCost}
                      agreements={group.factAgreementsCost}
                    />
                  </td>
                  <td
                    className={`plan-fact-td-num plan-fact-deviation ${
                      groupDev >= 0 ? "positive" : "negative"
                    }`}
                  >
                    {groupDev >= 0 ? "+" : "-"} {formatMoney(Math.abs(groupDev))}
                  </td>
                </tr>
                {expandedGroups.has(group.id) &&
                  (group.sections || []).map((sec) => {
                    const plan = Number(sec.planEstimate) || 0;
                    const fact = Number(sec.factCost) || 0;
                    const dev = plan - fact;
                    const rowId = sec.id;
                    const isSectionExpanded = expandedSections.has(rowId);
                    const sectionKey = detailKey(sec.sectionId);
                    const blocksData = sectionBlocks[sectionKey];
                    const isBlocksLoading =
                      !!sectionBlocksLoading[sectionKey] ||
                      (loading &&
                        isSectionExpanded &&
                        sec.isWorkingDesign &&
                        !blocksData);
                    const canExpand =
                      sec.isWorkingDesign ||
                      sec.sectionId != null ||
                      (sec.performers && sec.performers.length > 0) ||
                      hasNonZeroCost(sec.factCost) ||
                      hasNonZeroCost(sec.planEstimate);
                    const roleTimesheet = filterTimesheetByPerformers(
                      sectionTimesheets[sectionKey],
                      sec.performers,
                    );
                    const isTimesheetLoading =
                      !!sectionTimesheetsLoading[sectionKey] ||
                      (loading &&
                        isSectionExpanded &&
                        !sec.isWorkingDesign &&
                        !sectionTimesheets[sectionKey]);

                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          className={`plan-fact-section-row${
                            canExpand
                              ? " plan-fact-section-row--expandable"
                              : ""
                          }`}
                          onClick={() => toggleSection(sec)}
                        >
                          <td className="plan-fact-td-section">
                            {canExpand && (
                              <span
                                className={`plan-fact-expand-icon subsection ${
                                  isSectionExpanded ? "expanded" : ""
                                }`}
                              >
                                ▶
                              </span>
                            )}
                            {sec.name}
                          </td>
                          <td className="plan-fact-td-num">
                            {formatMoney(plan)}
                          </td>
                          <td className="plan-fact-td-num plan-fact-fact-cell">
                            <FactValue
                              value={fact}
                              reports={sec.factReportsCost}
                              agreements={sec.factAgreementsCost}
                            />
                          </td>
                          <td
                            className={`plan-fact-td-num plan-fact-deviation ${
                              dev >= 0 ? "positive" : "negative"
                            }`}
                          >
                            {dev >= 0 ? "+" : "-"} {formatMoney(Math.abs(dev))}
                          </td>
                        </tr>
                        {isSectionExpanded && sec.isWorkingDesign && (
                          <tr className="plan-fact-detail-row">
                            <td colSpan={4} className="plan-fact-detail-cell">
                              {isBlocksLoading ? (
                                <div className="plan-fact-blocks-loading">
                                  Загрузка блоков...
                                </div>
                              ) : blocksData?.blocks?.length > 0 ? (
                                <div className="plan-fact-performers">
                                  <table className="plan-fact-performers-table">
                                    <thead>
                                      <tr>
                                        <th></th>
                                        <th>Исполнитель</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                        <th>Трудозатраты</th>
                                        <th>Прямые затраты</th>
                                      </tr>
                                      <tr className="plan-fact-performers-subheader">
                                        <th></th>
                                        <th></th>
                                        <th
                                          colSpan={2}
                                          className="plan-fact-subheader-plan"
                                        >
                                          План
                                        </th>
                                        <th
                                          colSpan={2}
                                          className="plan-fact-subheader-fact"
                                        >
                                          Факт
                                        </th>
                                        <th
                                          colSpan={2}
                                          className="plan-fact-subheader-dev"
                                        >
                                          Отклонение
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {blocksData.blocks.map((blk) => {
                                        const blockId = blk.id ?? blk.block?.id;
                                        const blockName =
                                          blk.name ?? blk.block?.name ?? "Блок";
                                        const blockPlanDays = toNumber(
                                          blk.estimate_days ??
                                            blk.costs_per_block?.day_efforts,
                                        );
                                        const blockPlanCost = toNumber(
                                          blk.estimate_direct_cost ??
                                            blk.costs_per_block
                                              ?.money_expenditures,
                                        );
                                        const blockFactDays = toNumber(
                                          blk.fact_total_days ??
                                            blk.fact_days ??
                                            (blk.fact_total_hours != null
                                              ? Number(blk.fact_total_hours) / 8
                                              : null),
                                        );
                                        const blockFactCost = toNumber(
                                          blk.fact_total_cost ?? blk.fact_cost,
                                        );
                                        const blockReportsCost = toNumber(
                                          blk.fact_reports_cost,
                                        );
                                        const blockAgreementsCost = toNumber(
                                          blk.fact_agreements_cost,
                                        );
                                        const blockDevDays =
                                          blockPlanDays - blockFactDays;
                                        const blockDevCost =
                                          blockPlanCost - blockFactCost;
                                        const performers =
                                          blk.performers ||
                                          blk.block?.performers ||
                                          [];
                                        const performersAgreementsSum =
                                          performers.reduce(
                                            (sum, p) =>
                                              sum +
                                              toNumber(p.fact_agreements_cost),
                                            0,
                                          );
                                        const unlistedAgreementsCost =
                                          blockAgreementsCost -
                                          performersAgreementsSum;

                                        return (
                                          <React.Fragment
                                            key={blockId ?? blockName}
                                          >
                                            <tr className="plan-fact-block-header">
                                              <td colSpan={8}>{blockName}</td>
                                            </tr>
                                            <tr className="plan-fact-block-total">
                                              <td></td>
                                              <td>Все исполнители</td>
                                              <td>
                                                = {formatDays(blockPlanDays)}
                                              </td>
                                              <td>
                                                ~ {formatMoney(blockPlanCost)}
                                              </td>
                                              <td>
                                                = {formatDays(blockFactDays)}
                                              </td>
                                              <td>
                                                <FactValue
                                                  value={blockFactCost}
                                                  reports={blockReportsCost}
                                                  agreements={
                                                    blockAgreementsCost
                                                  }
                                                />
                                              </td>
                                              <td
                                                className={
                                                  blockDevDays >= 0
                                                    ? "positive"
                                                    : "negative"
                                                }
                                              >
                                                = {formatDays(blockDevDays)}
                                              </td>
                                              <td
                                                className={
                                                  blockDevCost >= 0
                                                    ? "positive"
                                                    : "negative"
                                                }
                                              >
                                                ~ {blockDevCost >= 0 ? "" : "-"}
                                                {formatMoney(
                                                  Math.abs(blockDevCost),
                                                )}
                                              </td>
                                            </tr>
                                            {performers.map((p, pi) => {
                                              const {
                                                reports,
                                                agreements,
                                                factCost,
                                                factDays,
                                                planPercent,
                                              } = getPerformerFact(p);
                                              const planDaysShare =
                                                planPercent != null
                                                  ? (blockPlanDays *
                                                      planPercent) /
                                                    100
                                                  : null;
                                              const planCostShare =
                                                planPercent != null
                                                  ? (blockPlanCost *
                                                      planPercent) /
                                                    100
                                                  : null;
                                              const hasFact =
                                                factDays != null ||
                                                factCost != null;
                                              const devDays =
                                                planDaysShare != null &&
                                                factDays != null
                                                  ? planDaysShare - factDays
                                                  : null;
                                              const devCost =
                                                planCostShare != null &&
                                                factCost != null
                                                  ? planCostShare - factCost
                                                  : null;

                                              return (
                                                <tr
                                                  key={`${blockId ?? blockName}-${p.employee_id ?? pi}`}
                                                  className={`plan-fact-performer-row${
                                                    hasNonZeroCost(agreements)
                                                      ? " plan-fact-subcontract-row"
                                                      : ""
                                                  }`}
                                                >
                                                  <td></td>
                                                  <td className="plan-fact-performer-name">
                                                    <span className="plan-fact-performer-bullet">
                                                      ○
                                                    </span>
                                                    {getPerformerName(p)}
                                                    {planPercent != null && (
                                                      <span className="plan-fact-performer-share">
                                                        доля {planPercent}%
                                                      </span>
                                                    )}
                                                    {hasNonZeroCost(
                                                      agreements,
                                                    ) && (
                                                      <span className="plan-fact-performer-share">
                                                        субподряд
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td>
                                                    {planDaysShare != null
                                                      ? `= ${formatDays(planDaysShare)}`
                                                      : "–"}
                                                  </td>
                                                  <td>
                                                    {planCostShare != null
                                                      ? `~ ${formatMoney(planCostShare)}`
                                                      : "–"}
                                                  </td>
                                                  {!hasFact ? (
                                                    <>
                                                      <td>–</td>
                                                      <td>–</td>
                                                      <td>–</td>
                                                      <td>–</td>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <td>
                                                        {factDays != null
                                                          ? `= ${formatDays(factDays)}`
                                                          : "–"}
                                                      </td>
                                                      <td>
                                                        {factCost != null ? (
                                                          <FactValue
                                                            value={factCost}
                                                            reports={reports}
                                                            agreements={
                                                              agreements
                                                            }
                                                          />
                                                        ) : (
                                                          "–"
                                                        )}
                                                      </td>
                                                      <td
                                                        className={
                                                          devDays != null
                                                            ? devDays >= 0
                                                              ? "positive"
                                                              : "negative"
                                                            : ""
                                                        }
                                                      >
                                                        {devDays != null
                                                          ? `= ${formatDays(devDays)}`
                                                          : "–"}
                                                      </td>
                                                      <td
                                                        className={
                                                          devCost != null
                                                            ? devCost >= 0
                                                              ? "positive"
                                                              : "negative"
                                                            : ""
                                                        }
                                                      >
                                                        {devCost != null
                                                          ? `~ ${
                                                              devCost >= 0
                                                                ? ""
                                                                : "-"
                                                            }${formatMoney(Math.abs(devCost))}`
                                                          : "–"}
                                                      </td>
                                                    </>
                                                  )}
                                                </tr>
                                              );
                                            })}
                                            {hasNonZeroCost(
                                              unlistedAgreementsCost,
                                            ) && (
                                              <tr className="plan-fact-performer-row plan-fact-subcontract-row">
                                                <td></td>
                                                <td className="plan-fact-performer-name">
                                                  <span className="plan-fact-performer-bullet">
                                                    ○
                                                  </span>
                                                  Субподрядчики
                                                </td>
                                                <td>–</td>
                                                <td>–</td>
                                                <td>–</td>
                                                <td>
                                                  ~{" "}
                                                  {formatMoney(
                                                    unlistedAgreementsCost,
                                                  )}
                                                </td>
                                                <td>–</td>
                                                <td>–</td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="plan-fact-no-blocks">
                                  Нет данных по блокам
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        {isSectionExpanded &&
                          !sec.isWorkingDesign &&
                          canExpand && (
                            <tr className="plan-fact-detail-row">
                              <td colSpan={4} className="plan-fact-detail-cell">
                                <div className="plan-fact-performers">
                                  <RolePerformersTable section={sec} />
                                  {!sec.skipTimesheet && (
                                    <FactTimesheetView
                                      data={roleTimesheet}
                                      loading={isTimesheetLoading}
                                      error={sectionTimesheetsError[sectionKey]}
                                      hideWhenEmpty
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
              );
            })}
            {unallocatedExpense != null && (
              <>
                <tr
                  className={`plan-fact-unallocated-row${
                    unallocatedExpense.sections?.length > 0
                      ? " plan-fact-unallocated-row--expandable"
                      : ""
                  }`}
                  onClick={() => {
                    if (!unallocatedExpense.sections?.length) return;
                    setExpandedSections((prev) => {
                      const next = new Set(prev);
                      if (next.has("unallocated")) next.delete("unallocated");
                      else next.add("unallocated");
                      return next;
                    });
                  }}
                >
                  <td className="plan-fact-td-section">
                    {unallocatedExpense.sections?.length > 0 && (
                      <span
                        className={`plan-fact-expand-icon ${
                          expandedSections.has("unallocated") ? "expanded" : ""
                        }`}
                      >
                        ▶
                      </span>
                    )}
                    {unallocatedExpense.name}
                  </td>
                  <td className="plan-fact-td-num">–</td>
                  <td className="plan-fact-td-num plan-fact-fact-cell">
                    <FactValue
                      value={unallocatedExpense.fact_total_cost}
                      reports={unallocatedExpense.fact_reports_cost}
                      agreements={unallocatedExpense.fact_agreements_cost}
                    />
                  </td>
                  <td
                    className={`plan-fact-td-num plan-fact-deviation ${
                      -unallocatedExpense.fact_total_cost >= 0
                        ? "positive"
                        : "negative"
                    }`}
                  >
                    {unallocatedExpense.fact_total_cost > 0 ? "- " : ""}
                    {formatMoney(Math.abs(unallocatedExpense.fact_total_cost))}
                  </td>
                </tr>
                {expandedSections.has("unallocated") &&
                  unallocatedExpense.sections.map((sec, idx) => {
                    const secFact = toNumber(sec.fact_total_cost);
                    const performers = Array.isArray(sec.performers)
                      ? sec.performers
                      : [];
                    const rowId = `unallocated-sec-${sec.section_id ?? idx}`;
                    const canExpandSection = performers.length > 0;
                    const isSectionExpanded = expandedSections.has(rowId);
                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          className={`plan-fact-unallocated-section-row${
                            canExpandSection
                              ? " plan-fact-unallocated-row--expandable"
                              : ""
                          }`}
                          onClick={() => {
                            if (!canExpandSection) return;
                            setExpandedSections((prev) => {
                              const next = new Set(prev);
                              if (next.has(rowId)) next.delete(rowId);
                              else next.add(rowId);
                              return next;
                            });
                          }}
                        >
                          <td className="plan-fact-td-section plan-fact-unallocated-section-name">
                            {canExpandSection && (
                              <span
                                className={`plan-fact-expand-icon ${
                                  isSectionExpanded ? "expanded" : ""
                                }`}
                              >
                                ▶
                              </span>
                            )}
                            {sec.section_name || "Раздел"}
                          </td>
                          <td className="plan-fact-td-num">–</td>
                          <td className="plan-fact-td-num plan-fact-fact-cell">
                            <FactValue
                              value={secFact}
                              reports={sec.fact_reports_cost}
                              agreements={sec.fact_agreements_cost}
                            />
                          </td>
                          <td
                            className={`plan-fact-td-num plan-fact-deviation ${
                              -secFact >= 0 ? "positive" : "negative"
                            }`}
                          >
                            {secFact > 0 ? "- " : ""}
                            {formatMoney(Math.abs(secFact))}
                          </td>
                        </tr>
                        {isSectionExpanded && (
                          <tr className="plan-fact-detail-row">
                            <td colSpan={4} className="plan-fact-detail-cell">
                              <div className="plan-fact-performers">
                                <RolePerformersTable
                                  showPlan={false}
                                  section={{
                                    performers,
                                    factDays: sec.fact_total_days,
                                    factHours: sec.fact_total_hours,
                                    factCost: secFact,
                                    factReportsCost: sec.fact_reports_cost,
                                    factAgreementsCost:
                                      sec.fact_agreements_cost,
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </>
            )}
          </tbody>
          <tfoot>
            <tr className="plan-fact-total-row">
              <td className="plan-fact-td-section">ВСЕ РАЗДЕЛЫ</td>
              <td className="plan-fact-td-num">
                {formatMoney(contractEstimateTotal)}
              </td>
              <td className="plan-fact-td-num">
                <FactValue
                  value={factTotal}
                  reports={factReportsCost}
                  agreements={factAgreementsCost}
                  prefix=""
                />
              </td>
              <td
                className={`plan-fact-td-num plan-fact-deviation ${
                  deviationTotal >= 0 ? "positive" : "negative"
                }`}
              >
                {formatMoney(deviationTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      </div>

      {/* <div className="plan-fact-actions">
        <button type="button" className="plan-fact-btn plan-fact-btn-pdf">
          Печать PDF
        </button>
      </div> */}
    </div>
  );
};

export default PlanFactAnalysis;
